import React, { useState, useEffect, useCallback } from 'react';
import { Users, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

// Same interaction as the brand TeamShareModal (BrandIdentitiesPage.jsx),
// adapted to projects: /api/projects/<id>/members.
const MEMBER_ROLES = [
  { value: 'viewer', label: 'Viewer', desc: 'Can open and view' },
  { value: 'editor', label: 'Editor', desc: 'Can also edit and save' },
];

export default function ProjectShareModal({ project, userId, onClose }) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteError, setInviteError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(withBackend(`/api/projects/${project.id}/members?user_id=${userId}`));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load members');
      setMembers(data.members || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  }, [project.id, userId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleAdd = async () => {
    const email = inviteEmail.trim();
    if (!email || adding) return;
    setAdding(true);
    setInviteError(null);
    try {
      const res = await fetch(withBackend(`/api/projects/${project.id}/members`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, email, role: inviteRole }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInviteError(data.error || 'Failed to add member');
        return;
      }
      setInviteEmail('');
      toast.success(`Shared with ${email}`);
      fetchMembers();
    } catch (err) {
      setInviteError('Something went wrong — please try again');
      toast.error(err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (member, role) => {
    if (role === member.role) return;
    setUpdatingMemberId(member.id);
    try {
      const res = await fetch(withBackend(`/api/projects/${project.id}/members/${member.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, ...(data.member || { role }) } : m)));
      toast.success(`${member.email} is now ${role === 'editor' ? 'an editor' : 'a viewer'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemove = async (member) => {
    setRemovingMemberId(member.id);
    try {
      const res = await fetch(withBackend(`/api/projects/${project.id}/members/${member.id}?user_id=${userId}`), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to remove member');
      setMembers(prev => prev.filter(m => m.id !== member.id));
      toast.success(`Removed ${member.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const selectedRole = MEMBER_ROLES.find(r => r.value === inviteRole);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-2 min-w-0">
            <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground">Share "{project.name}" with your team</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Teammates open and work on this same generation from their own account.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Add member row */}
        <div className="space-y-1.5 mb-4">
          <div className="flex gap-2">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); if (inviteError) setInviteError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="teammate@company.com"
              className="flex-1 h-9 text-sm"
              disabled={adding}
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              disabled={adding}
              className="h-9 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <Button size="sm" className="h-9 px-4" onClick={handleAdd} disabled={adding || !inviteEmail.trim()}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
          {inviteError ? (
            <p className="text-xs text-destructive">{inviteError}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground">{selectedRole?.label}: {selectedRole?.desc}</p>
          )}
        </div>

        {/* Member list */}
        <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
          {loadingMembers ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Not shared with anyone yet.</p>
          ) : (
            members.map((m) => (
              <div key={m.id} className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 min-w-0 text-sm text-foreground truncate" title={m.email}>{m.email}</span>
                {updatingMemberId === m.id && <Loader2 className="w-3 h-3 text-muted-foreground animate-spin flex-shrink-0" />}
                <select
                  value={m.role}
                  onChange={(e) => handleRoleChange(m, e.target.value)}
                  disabled={updatingMemberId === m.id || removingMemberId === m.id}
                  className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button
                  onClick={() => handleRemove(m)}
                  disabled={removingMemberId === m.id || updatingMemberId === m.id}
                  className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                  title="Remove access"
                >
                  {removingMemberId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
